import { useMutation } from '@apollo/client/react';
import { AuthenticateStytchPasswordDocument } from '@graphql/mutations/auth/authenticate-stytch-password.graphql';
import { ChangeStytchPasswordDocument } from '@graphql/mutations/auth/change-stytch-password.graphql';
import { OtpAuthenticateDocument } from '@graphql/mutations/auth/otp-authenticate.graphql';
import { OtpLoginOrCreateDocument } from '@graphql/mutations/auth/otp-login-or-create.graphql';
import { SetLoginPreferenceDocument } from '@graphql/mutations/auth/set-login-preference.graphql';
import { SetStytchPasswordDocument } from '@graphql/mutations/auth/set-stytch-password.graphql';
import { UnlinkProviderDocument } from '@graphql/mutations/auth/unlink-provider.graphql';
import {
  UserAuthProfileDocument,
  type UserAuthProfileQuery,
} from '@graphql/queries/auth/user-auth-profile.graphql';
import {
  UserPasswordProviderDocument,
  type UserPasswordProviderQuery,
} from '@graphql/queries/auth/user-password-provider.graphql';
import { type FirebaseUser, auth } from '@modules/firebase';
import { createChildLogger } from '@modules/logger';
import { apolloClient } from '@services/apollo';
import {
  type AuthError,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider as OAuth2Provider,
  linkWithCredential,
  linkWithRedirect,
  onIdTokenChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { useEffect } from 'react';

import { authService } from './firebase-auth/service';
import type { UserDetails } from './user-profile/user-profile-details-validator';

export enum AuthConstants {
  LS_LOGIN_EMAIL = 'emailForSignIn',
  LS_SSO_LOGIN = 'isSSOLogin',
  LS_SIGNUP_TYPE = 'signUpType',
  LS_SSO_METHOD = 'ssoMethod',
  /** sessionStorage: the email typed before an OAuth redirect, restored if the provider rejects the sign-in. */
  SS_SSO_EMAIL = 'ssoEmail',
}

export enum LoginMethod {
  ANONYMOUS = 'anonymous',
  PASSWORD = 'password',
  EMAIL_OTP = 'emailOtp',
  APPLE = 'apple.com',
  GOOGLE = 'google.com',
  MICROSOFT = 'microsoft.com',
  OAUTH2PROXY = 'oidc.oauth2proxy',
  NEW_USER = 'new_user',
}

const logger = createChildLogger({ module: 'modules/auth-helpers' });
const LEGACY_TOKEN_STORAGE_KEY = 'token';

export type OAuthProvider = 'google.com' | 'microsoft.com' | 'apple.com' | 'oidc.oauth2proxy';

export type LoginMethodPayload = {
  type: 'otp_signin' | 'otp_signup';
  isExistingUser: boolean;
  methodId: LoginMethod | null;
  loginPreference?: string;
  ssoEnforced: boolean;
  publicEmailAddress?: boolean;
  enabledMethods?: string[];
  defaultMethod?: LoginMethod | null;
  /** Which system authenticates this user's password. When 'stytch', use the Stytch password flow. */
  passwordProvider?: 'firebase' | 'stytch';
};

function getOAuthProvider(provider: OAuthProvider): OAuth2Provider | GoogleAuthProvider {
  localStorage.setItem(AuthConstants.LS_SSO_METHOD, provider);
  switch (provider) {
    case 'google.com':
      return new GoogleAuthProvider();
    case 'microsoft.com':
    case 'apple.com':
    case 'oidc.oauth2proxy':
      return new OAuth2Provider(provider);
  }
}

function getMessageFromErrorCode(err: AuthError | undefined) {
  switch (err?.code) {
    case 'auth/invalid-email':
      return 'The email address provided is not valid';
    // Firebase returns invalid-credential (not wrong-password) once email-enumeration protection is on.
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Wrong password';
    case 'auth/user-not-found':
      return 'There is no Blinq account associated with this email address. Click the sign up button to create a new account instead.';
    case 'auth/network-request-failed':
      return 'We are sorry, something went wrong with the login process. Please try again in 1 to 2 minutes.';
    case 'auth/account-exists-with-different-credential':
      return `Your ${formatSSOProvider(
        localStorage.getItem(AuthConstants.LS_SSO_METHOD),
      )} has already been used to sign into an existing Blinq account. If you still want to create a new Blinq account you will need to use a different method.`;
    case 'auth/email-already-in-use':
      return 'This email address is already in use by another account, please sign in to that account.';
    case 'auth/invalid-action-code':
      return 'This login link has expired. Please click the button below to go back to the login screen.';
    case 'auth/credential-already-in-use':
      return `Your ${formatSSOProvider(
        localStorage.getItem(AuthConstants.LS_SSO_METHOD),
      )} has already been used to sign into an existing Blinq account. If you still want to create a new Blinq account you will need to use a different method.`;
    case 'auth/timeout':
      return 'The operation timed out, please try again.';
    case 'auth/weak-password':
      return 'The password is too weak. Please use at least 10 characters.';
    default:
      return 'An error occurred, please try again.';
  }
}

function isNotUserError(err: AuthError | undefined) {
  switch (err?.code) {
    case 'auth/invalid-email':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/credential-already-in-use':
    case 'auth/weak-password':
    case 'auth/account-exists-with-different-credential':
      return false;
    case 'auth/network-request-failed':
    case 'auth/invalid-action-code':
    case 'auth/timeout':
    default:
      return true;
  }
}

function formatSSOProvider(provider: string | null) {
  switch (provider) {
    case 'google.com':
      return 'Google account';
    case 'microsoft.com':
      return 'Microsoft account';
    case 'apple.com':
      return 'Apple ID';
    default:
      return 'account';
  }
}

export function handleFirebaseAuthError(err: unknown, log = logger): AuthError {
  const error = err as AuthError;
  if (isNotUserError(error)) {
    log.error('Firebase Auth Error', { error });
  }
  return {
    ...error,
    code: error?.code || 'unknown',
    message: getMessageFromErrorCode(error),
  };
}

export function setSignUpType(anonymous: boolean): void {
  localStorage.setItem(AuthConstants.LS_SIGNUP_TYPE, anonymous ? 'true' : 'false');
}

export function getSignUpType(): boolean | undefined {
  const signUpType = localStorage.getItem(AuthConstants.LS_SIGNUP_TYPE);
  if (signUpType) {
    return signUpType === 'true';
  }
  return undefined;
}

export function clearSignUpType(): void {
  localStorage.removeItem(AuthConstants.LS_SIGNUP_TYPE);
}

/**
 * Maps a LoginMethod to an OAuthProvider so it can be using in methods like `loginWithSocialProvider`
 * Throws an error if the LoginMethod does not map to an OAuthProvider
 *
 * @param loginMethod LoginMethod
 * @returns OAuthProvider
 */
export function mapLoginMethodToOAuthProvider(loginMethod: LoginMethod): OAuthProvider {
  switch (loginMethod) {
    case LoginMethod.GOOGLE:
      return 'google.com';
    case LoginMethod.MICROSOFT:
      return 'microsoft.com';
    case LoginMethod.APPLE:
      return 'apple.com';
    case LoginMethod.OAUTH2PROXY:
      return 'oidc.oauth2proxy';
    default:
      throw new Error('Invalid login method does not map to an OAuthProvider');
  }
}

/**
 * Creates a new login with email and password that is attached to the current user
 * @param email string
 * @param password string
 * @returns FirebaseUser
 */
export async function addEmailAndPasswordLoginToCurrentUser(
  email: string,
  password: string,
): Promise<FirebaseUser | null> {
  try {
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    const credential = EmailAuthProvider.credential(email, password);

    await linkWithCredential(user, credential);

    await authService.signInWithEmailAndPassword(email, password);
    const updatedUser = auth.currentUser;
    return updatedUser;
  } catch (error) {
    logger.error('Failed to create Email Auth user', { error });
    throw handleFirebaseAuthError(error);
  }
}

/**
 * Used to attach a social provider to the current identity
 *
 * @param provider OAuthProvider
 * @returns Promise<void>
 */
export async function addSocialProviderLoginToCurrentUser(provider: OAuthProvider): Promise<void> {
  try {
    const user = auth.currentUser;

    if (!user) {
      return;
    }
    const oauthProvider = getOAuthProvider(provider);
    // Used to set login loading state immediately upon return
    localStorage.setItem(AuthConstants.LS_SSO_LOGIN, '1');

    return linkWithRedirect(user, oauthProvider);
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * Resolves the password provider via a separate, best-effort query so the critical UserAuthProfile
 * routing query stays compatible with an API deployed before this field existed. Any failure (including
 * the field being absent on an older API) defaults to 'firebase' — safe because passwords are
 * dual-written, so a Stytch-migrated user still signs in via the Firebase path when this is unavailable.
 */
async function getPasswordProvider(email: string): Promise<'firebase' | 'stytch'> {
  try {
    const { data } = await apolloClient.query<UserPasswordProviderQuery>({
      query: UserPasswordProviderDocument,
      variables: { email },
      context: { isUnauthenticatedRequest: true },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });
    return data?.userAuthProfile?.passwordProvider === 'stytch' ? 'stytch' : 'firebase';
  } catch {
    return 'firebase';
  }
}

async function getUserAuthProfile(email: string) {
  const [{ data }, passwordProvider] = await Promise.all([
    apolloClient.query<UserAuthProfileQuery>({
      query: UserAuthProfileDocument,
      variables: { email },
      context: { isUnauthenticatedRequest: true },
      fetchPolicy: 'network-only',
    }),
    getPasswordProvider(email),
  ]);

  if (!data?.userAuthProfile) {
    throw new Error('User auth profile not found');
  }

  return {
    isSsoEnforced: data.userAuthProfile.defaultMethod === 'oidc.oauth2proxy',
    defaultMethod: data.userAuthProfile.defaultMethod,
    enabledMethods: data.userAuthProfile.enabledMethods,
    isExistingUser: data.userAuthProfile.isExistingUser,
    lastUsedMethod: data.userAuthProfile.lastUsedMethod,
    isPublicEmail: data.userAuthProfile.isPublicEmail,
    passwordProvider,
  };
}

const methodmap = new Map<string, LoginMethod | null>([
  ['password', LoginMethod.PASSWORD],
  ['emailOtp', LoginMethod.EMAIL_OTP],
  ['google.com', LoginMethod.GOOGLE],
  ['apple.com', LoginMethod.APPLE],
  ['microsoft.com', LoginMethod.MICROSOFT],
  ['oidc.oauth2proxy', LoginMethod.OAUTH2PROXY],
]);

const getMethod = (method: string) => methodmap.get(method) ?? null;

/**
 * Returns the single highest priority login method for a given email address
 * @param email string
 * @returns a LoginMethodPayload
 */
export async function getLoginMethod(email: string): Promise<LoginMethodPayload> {
  try {
    const userAuthProfile = await getUserAuthProfile(email);

    const methodId = userAuthProfile.isExistingUser
      ? getMethod(userAuthProfile.lastUsedMethod || userAuthProfile.defaultMethod)
      : LoginMethod.NEW_USER;

    return {
      methodId,
      type: userAuthProfile.isExistingUser ? 'otp_signin' : 'otp_signup',
      isExistingUser: userAuthProfile.isExistingUser,
      defaultMethod: getMethod(userAuthProfile.defaultMethod),
      ssoEnforced: userAuthProfile.isSsoEnforced,
      publicEmailAddress: userAuthProfile.isPublicEmail,
      loginPreference: userAuthProfile.lastUsedMethod || undefined,
      enabledMethods: userAuthProfile.enabledMethods,
      passwordProvider: userAuthProfile.passwordProvider,
    };
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * Sends a password reset email to the user.
 *
 * SECURITY — incomplete for Stytch-migrated users (`password_provider === 'stytch'`): this resets the
 * Firebase password only, so the user's old password stays valid in Stytch and remains usable via the
 * Stytch-first sign-in fallback. Before the `stytch-password` flag is fully rolled out, this must branch
 * on the password provider and revoke/overwrite the Stytch credential for migrated users (e.g. route them
 * through an OTP- or email-verified reset that resets Stytch and dual-writes Firebase).
 *
 * @param email string
 */
export async function sendResetLink(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * Chooses the best login method for the email address supplied
 *
 * @param email string
 * @returns LoginMethod
 */
export async function pickLoginMethodForEmail(email: string): Promise<LoginMethodPayload> {
  try {
    if (!email) {
      return {
        methodId: LoginMethod.NEW_USER,
        ssoEnforced: false,
        isExistingUser: false,
        type: 'otp_signup',
      };
    }

    return await getLoginMethod(email);
  } catch {
    return {
      methodId: LoginMethod.NEW_USER,
      ssoEnforced: false,
      isExistingUser: false,
      type: 'otp_signup',
    };
  }
}

/**
 * Wraps the Firebase getIdToken method.
 *
 * @param forceRefresh always retrieve a new token
 * @returns a JWT string
 */
export async function getToken(forceRefresh?: true): Promise<string | null> {
  // this is to guard against cases where Apollo Client is used server-side
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    await auth.authStateReady();
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    return await user.getIdToken(forceRefresh);
  } catch (err) {
    throw handleFirebaseAuthError(err);
  }
}

/**
 * This function is mainly to check if a firebase user is truly anon in our system. Since
 * if the user is signed in using the custom (i.e. through session continuity) then is
 * isAnonymous will be false for that user even though the user does not have any credential
 * linked to them
 * @param user firebase user
 * @returns
 */
export function isIdentityBelongToAnonUser(firebaseUser: FirebaseUser | null | undefined): boolean {
  // If Firebase hasn't loaded yet, treat as anonymous user (safe default)
  if (!firebaseUser) {
    return true;
  }

  const isFirebaseUserAnonymous = firebaseUser.isAnonymous;

  // when the user signed in with Blinq's custom token, the user will have an empty provider data
  const isUserSignedInWithBlinqCustomToken =
    !isFirebaseUserAnonymous && firebaseUser.providerData.length === 0;

  return isFirebaseUserAnonymous || isUserSignedInWithBlinqCustomToken;
}

// Login handlers

export type LoginEventCallback = (userInfo: UserInfo | null) => void;

export type UserInfo =
  | {
      isAnonUser: false;
      user: FirebaseUser;
      userDetails: UserDetails;
      /** Server-derived: no Blinq user existed when this session was started. */
      isNewUser: boolean;
    }
  | {
      user: FirebaseUser;
      isAnonUser: true;
      userDetails?: never;
      isNewUser?: never;
    };

const listeners = new Set<LoginEventCallback>();

/**
 * Object that manages authentication event listeners and provides methods to subscribe to login events.
 * This is used to notify components when a user's authentication state changes.
 */
export const authEvents = {
  /**
   * Subscribe to login events. The callback will be called whenever a user logs in or out.
   * @param callback - Function to be called with user information when auth state changes
   * @returns A cleanup function that removes the callback from the listeners
   */
  onLogin(callback: LoginEventCallback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
  },
  /**
   * Emits a login event to all registered listeners with the current user information.
   * @param userInfo - The current user information, or null if the user is logged out
   */
  emitLogin(userInfo: UserInfo | null) {
    // listener_count is the signal that matters here: a sign-in that completes while no
    // subscriber is mounted emits into an empty set, the event is dropped on the floor, and
    // whatever was waiting on it (e.g. LoginForm's spinner) never resolves.
    logger.info('Emitting login event.', {
      listener_count: listeners.size,
      has_user_info: !!userInfo,
      user_id: userInfo?.user.uid ?? null,
      is_anon_user: userInfo?.isAnonUser ?? null,
    });
    listeners.forEach((cb) => cb(userInfo));
  },
};

/**
 * Do not use this function, it is already mounted in _app.tsx
 *
 * This function provides a way to listen to auth state changes
 * and start the user session after login.
 */
export function useOnIdTokenChangedObserver(): void {
  useEffect(() => {
    // onIdTokenChanged fires on subscribe, on sign-in/out, and on every token refresh. The
    // sequence number turns a Datadog session into an ordered trace rather than a pile of
    // identical lines, and makes a refresh loop obvious at a glance.
    let sequence = 0;

    return onIdTokenChanged(auth, async (user) => {
      sequence += 1;
      const log = logger.child({
        observer: 'onIdTokenChanged',
        sequence,
        user_id: user?.uid ?? null,
        is_anon_user: user ? isIdentityBelongToAnonUser(user) : null,
      });

      log.info('Auth observer fired.', { has_user: !!user });

      localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);

      if (!user) {
        const logoutStart = performance.now();
        const logoutResponse = await fetch('/api/session/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        log.info('Session logout request completed.', {
          duration_ms: Math.round(performance.now() - logoutStart),
          status: logoutResponse.status,
          ok: logoutResponse.ok,
        });

        authEvents.emitLogin(null);

        return;
      }

      const tokenStart = performance.now();
      let token: string;
      try {
        token = await user.getIdToken();
      } catch (error) {
        log.error('Failed to get Firebase ID token.', {
          duration_ms: Math.round(performance.now() - tokenStart),
          error,
        });
        throw error;
      }

      log.info('Got Firebase ID token.', {
        duration_ms: Math.round(performance.now() - tokenStart),
      });

      try {
        const isAnonUser = isIdentityBelongToAnonUser(user);

        // Start user session after login, set's session cookie, and token cookie
        const loginStart = performance.now();
        const userDetailsResponse = await fetch('/api/session/login', {
          method: 'POST',
          body: JSON.stringify({ token, isAnonUser }),
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        log.info('Session login request completed.', {
          duration_ms: Math.round(performance.now() - loginStart),
          status: userDetailsResponse.status,
          ok: userDetailsResponse.ok,
        });

        if (!userDetailsResponse.ok) {
          throw new Error('Failed to fetch user details');
        }

        if (isAnonUser) {
          authEvents.emitLogin({
            user,
            isAnonUser,
          });
          return;
        }

        const { isNewUser = false, ...userDetails } =
          (await userDetailsResponse.json()) as UserDetails & { isNewUser?: boolean };

        authEvents.emitLogin({
          user,
          isAnonUser,
          userDetails,
          isNewUser,
        });
      } catch (error) {
        log.error('Failed to start user session', { error });
        authEvents.emitLogin(null);
      }
    });
  }, []);
}

export class OtpLoginOrCreateError extends Error {
  code: string;

  constructor(code: string, message?: string) {
    super(message);
    this.code = code;
  }
}

export function useOtpLoginOrCreate(requestId: string) {
  const [otpLoginOrCreate, { data, loading, error }] = useMutation(OtpLoginOrCreateDocument, {
    context: { isUnauthenticatedRequest: true, headers: { 'x-blinq-otp-request-id': requestId } },
  });

  async function callMutation(email: string): Promise<string> {
    const { data } = await otpLoginOrCreate({ variables: { input: { email } } });

    const responseData = data?.otpLoginOrCreate;
    if (!responseData) {
      throw new OtpLoginOrCreateError('unknown_error');
    }

    if (responseData.__typename === 'OtpLoginOrCreateResponse') {
      return responseData.methodId;
    } else if (responseData.__typename === 'OtpServiceError') {
      throw new OtpLoginOrCreateError(responseData.code ?? 'unknown_error', responseData.message);
    }
    throw new OtpLoginOrCreateError('unknown_error');
  }

  return [callMutation, { data, loading, error }] as const;
}

export class OtpAuthenticateError extends Error {
  code: string;

  constructor(code: string, message?: string) {
    super(message);
    this.code = code;
  }
}

export function useOtpAuthenticate(requestId: string) {
  const [otpAuthenticate, { data, loading, error }] = useMutation(OtpAuthenticateDocument, {
    context: { isUnauthenticatedRequest: true, headers: { 'x-blinq-otp-request-id': requestId } },
  });

  async function callMutation(methodId: string, code: string) {
    const result = await otpAuthenticate({ variables: { input: { methodId, code } } });

    const responseData = result.data?.otpAuthenticate;
    if (responseData) {
      if (responseData.__typename === 'OtpAuthenticateResponse') {
        return responseData.token;
      } else if (responseData.__typename === 'OtpServiceError') {
        throw new OtpAuthenticateError(responseData.code ?? 'unknown', responseData.message);
      }
      throw new OtpAuthenticateError('unknown_error');
    } else {
      throw new OtpAuthenticateError('unknown_error');
    }
  }
  return [callMutation, { data, loading, error }] as const;
}

export function useUnlinkProvider() {
  const [unlinkProvider] = useMutation(UnlinkProviderDocument);

  return async (provider: string) => {
    await unlinkProvider({ variables: { input: { provider } } });
  };
}

export function useSetLoginPreference() {
  const [setLoginPreference, { data, loading, error }] = useMutation(SetLoginPreferenceDocument);

  async function callMutation(method: string | null) {
    if (!method) {
      logger.warn('No method provided to setLoginPreference');
      return;
    }
    await setLoginPreference({ variables: { input: { method } } });
  }

  return [callMutation, { data, loading, error }] as const;
}

export class StytchPasswordError extends Error {
  code: string;

  constructor(code: string, message?: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Maps a Stytch password error code to sign-in copy. A StytchPasswordError's raw `message` is an
 * internal string ("Stytch API error"), so callers should use this instead of surfacing it directly.
 */
export function getStytchLoginErrorMessage(code: string): string {
  switch (code) {
    case 'too_many_requests':
    case 'user_lock_limit_reached':
      return 'Your access has been temporarily restricted. Please try again later.';
    case 'reset_password':
      return 'For your security, please reset your password to continue.';
    default:
      return 'An error occurred, please try again.';
  }
}

/**
 * Sign-in copy for `auth/provider-already-linked`. On a plain (non-link) sign-in, Firebase returns this
 * when the account already holds this provider under a different identity — e.g. the user's Google
 * Workspace account was deleted and recreated with the same email, so the new Google id can't attach.
 * The email code still resolves to the same account, so we point the user there instead.
 */
export function getProviderMismatchMessage(provider: string | null): string {
  return `This ${formatSSOProvider(provider)} doesn't match the one linked to your Blinq account. Continue with a code sent to your email instead.`;
}

/** True for our GraphQL per-IP rate limit, which surfaces as a plain "Rate limit exceeded" error. */
export function isRateLimitError(error: unknown): boolean {
  return error instanceof Error && /rate limit exceeded/i.test(error.message);
}

/**
 * Authenticates an email + password against Stytch (for users whose password lives in Stytch) and
 * returns a Firebase custom token to sign in with via `authService.signInWithCustomToken`.
 */
export function useAuthenticateStytchPassword() {
  const [authenticate, { loading }] = useMutation(AuthenticateStytchPasswordDocument, {
    context: { isUnauthenticatedRequest: true },
  });

  async function callMutation(email: string, password: string): Promise<string> {
    const result = await authenticate({ variables: { input: { email, password } } });
    const data = result.data?.authenticateStytchPassword;
    if (data?.__typename === 'StytchPasswordAuthenticateResponse') {
      return data.token;
    }
    if (data?.__typename === 'StytchPasswordError') {
      throw new StytchPasswordError(data.code ?? 'unknown_error', data.message);
    }
    throw new StytchPasswordError('unknown_error');
  }

  return [callMutation, { loading }] as const;
}

/**
 * Seeds (or re-seeds) the current authenticated user's password into Stytch. Used to enrol a new
 * password and to lazily migrate an existing Firebase password on next sign-in.
 */
export function useSetStytchPassword() {
  const [setStytchPassword, { loading }] = useMutation(SetStytchPasswordDocument);

  async function callMutation(password: string): Promise<void> {
    const result = await setStytchPassword({ variables: { input: { password } } });
    const data = result.data?.setStytchPassword;
    if (data?.__typename === 'StytchPasswordSetResponse') {
      return;
    }
    if (data?.__typename === 'StytchPasswordError') {
      throw new StytchPasswordError(data.code ?? 'unknown_error', data.message);
    }
    throw new StytchPasswordError('unknown_error');
  }

  return [callMutation, { loading }] as const;
}

/** Changes the current authenticated user's Stytch password (requires the current password). */
export function useChangeStytchPassword() {
  const [changeStytchPassword, { loading }] = useMutation(ChangeStytchPasswordDocument);

  async function callMutation(existingPassword: string, newPassword: string): Promise<void> {
    const result = await changeStytchPassword({
      variables: { input: { existingPassword, newPassword } },
    });
    const data = result.data?.changeStytchPassword;
    if (data?.__typename === 'StytchPasswordSetResponse') {
      return;
    }
    if (data?.__typename === 'StytchPasswordError') {
      throw new StytchPasswordError(data.code ?? 'unknown_error', data.message);
    }
    throw new StytchPasswordError('unknown_error');
  }

  return [callMutation, { loading }] as const;
}

export const loginOrCreateErrors = new Map<string, string>([
  [
    'invalid_email',
    'It looks like the email address you entered is not valid. Please check and try again.',
  ],
  ['access_restricted', 'Your access has been temporarily restricted. Please try again later.'],
]);
